import {organizationApiClient} from 'organization/organizationApiClient'
import {useOrganization} from 'organization/providers/OrganizationProvider'
import React, {useEffect, useState} from 'react'

export type BalanceType = 'income' | 'expense'

export type Balance = {
  type: BalanceType
  id: number
  description: string
  is_paid: boolean
  refunded: boolean
  price: number
  application_fee: number
  created_at: Date
  is_holded?: boolean
  ticket_tier_name?: string | null
}

export interface EarningContextProps {
  totalProfit: number
  ticketSales: number
  platformTax: number
  balance: Balance[]
  loading: boolean
  page: number
  totalPages: number
  changePage: (e: any, page: number) => void
  search: (term: string) => void
}

export const EarningContext = React.createContext<
  EarningContextProps | undefined
>(undefined)

export default function EarningProvider(props: {children: React.ReactElement}) {
  const apiClient = organizationApiClient()
  const [totalProfit, setTotalProfit] = useState<number>(0)
  const [ticketSales, setTicketSales] = useState<number>(0)
  const [platformTax, setPlatformTax] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(true)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [page, setPage] = useState<number>(1)
  const [balance, setBalance] = useState<Balance[]>([])

  const {organization} = useOrganization()

  useEffect(() => {
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const get = async (getPage?: number, searchTerm?: string) => {
    const selectedPage = getPage ?? page
    const search = searchTerm ?? ''

    await apiClient
      .get(
        `api/organization/${organization?.id}/earning?page=${selectedPage}&search=${search}`,
      )
      .then((response) => {
        setTotalProfit(response.total_profit)
        setTicketSales(response.ticket_sales)
        setPlatformTax(response.platform_tax)
        setBalance(response.data)
        setTotalPages(response.last_page)
        setPage(response.current_page)
      })
      .finally(() => setLoading(false))
  }

  const changePage = (e: any, toPage: number) => {
    setPage(toPage)
    get(toPage)
  }

  const search = async (term: string) => {
    return get(1, term)
  }

  // const balance = [
  //   {
  //     type: 'income' as BalanceType,
  //     from: 'ticket' as BalanceFrom,
  //     id: 1,
  //     price: 20,
  //   },
  //   {
  //     type: 'expense' as BalanceType,
  //     from: 'withdraw' as BalanceFrom,
  //     id: 2,
  //     price: 20,
  //   },
  // ]

  return (
    <EarningContext.Provider
      value={{
        totalProfit,
        ticketSales,
        platformTax,
        balance,
        loading,
        page,
        changePage,
        totalPages,
        search,
      }}
    >
      {props.children}
    </EarningContext.Provider>
  )
}

export function useEarning() {
  const context = React.useContext(EarningContext)
  if (context === undefined) {
    throw new Error('useEarning must be used within EarningProvider')
  }
  return context
}
